import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Card from "@mui/material/Card";
import SoftTypography from "components/SoftTypography";
import moment from "moment";
import SoftBox from "components/SoftBox";
import DataTable from "cxfcomponents/Tables/DataTable";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import ShareUnshareDialog from "cxfcomponents/ShareUnshareDialog";
import { IntegrationsMenu } from "./components/menu";
import { useIntegrations } from "hooks/useIntegrations";
import defaultIntegrationIcon from "assets/images/integration.svg";
import StatusBadge from "cxfcomponents/StatusBadge";
import Tiles from "./components/tiles";
import ExternalUsersShareDialog from "cxfcomponents/ExternalUsersShareDialog";
import { usePermissions } from "hooks/usePermissions";
import { setOpenShareUnshareIntegrationDialog } from "redux/actions/portal/integrationActionCreator";
import { useAuth0 } from "@auth0/auth0-react";
import { insertSharedIntegration, deleteSharedIntegrations } from "api/Integrations";
import {
  setExternalUsersShareDialogOpen,
  setShareItemData
} from "redux/actions/portal/sharingActionCreator";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PERMISSIONS } from "constants/portal";
import { UpdateIntegrationDialog } from "cxfcomponents/Integrations/UpdateIntegrationDialog";
import { Link } from "react-router-dom";
import { useIsOwner } from "hooks/useIsOwner";
import { permissionChecker } from "helpers/common";
import { useResourceListener } from "hooks/useResourceListener";

const options = {
  status: [{ value: "active", label: "Active" }]
};

const hasCustomIntegrationLogo = (logo) => {
  if (typeof logo === "string") {
    return logo.trim() !== "" && logo !== "null";
  }

  return Boolean(logo);
};

export const LibraryIntegrations = () => {
  const {
    integrations,
    handleFetchIntegrations,
    determineShareStatus,
    sharedIntegrations,
    handleFetchSharedIntegrations,
    canEditIntegration
  } = useIntegrations();

  const [tableView, setTableView] = useState("list");
  const { userPermissions } = usePermissions();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [availableShareUsers, setAvailableShareUsers] = useState([]);
  const [isShareMode, setIsShareMode] = useState(false);
  const { isExternalUsersShareDialogOpen } = useSelector(
    (store) => store.portal.sharing
  );
  const { users } = useSelector((store) => store.portal.users);
  const { openShareUnshareDialog } = useSelector((store) => store.portal.integrations);
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const activeUsers = users?.filter((u) => u.status === "active");
  const [showUpdateIntegrationDialog, setShowUpdateIntegrationDialog] = useState(false);
  const isOwner = useIsOwner();
  useResourceListener("integrationsSharingUpdate", handleReloadIntegrations);

  const handleCloseShareUnshareIntegrationDialog = () => {
    dispatch(setOpenShareUnshareIntegrationDialog(false));
    setSelectedIntegration(null);
    setAvailableShareUsers([]);
    setIsShareMode(false);
  };

  const handleShareIntegration = async (selectedUsers, setSelectedUsers) => {
    const shareIntegrationInput = selectedUsers.map((u) => ({
      ...u,
      integration_id: selectedIntegration.id,
      tenant_id: user?.stripeId,
      owner_id: user?.cxfUserId,
      shared_tenant_id: user?.stripeId
    }));

    const shareIntegrationResponse = await insertSharedIntegration(
      user?.stripeId,
      shareIntegrationInput
    );

    if (shareIntegrationResponse?.isSuccessful) {
      toast.success(
        `Integration ${selectedIntegration.integration_name} has been shared successfully!`
      );
    } else {
      toast.error(
        `Integration ${selectedIntegration.integration_name} has been shared successfully!`
      );
    }

    handleCloseShareUnshareIntegrationDialog();
    setSelectedUsers([]);
  };

  const handleUnshareIntegration = async (selectedUsers, setSelectedUsers) => {
    const unshareSharedIntegrations = selectedUsers.map((u) => ({
      shared_integration_id: sharedIntegrations?.find((si) => {
        return (
          si.integration_id === selectedIntegration.id && si.shared_user_id === u.shared_user_id
        );
      })?.id
    }));

    const unshareIntegrationResponse = await deleteSharedIntegrations(
      user?.stripeId,
      unshareSharedIntegrations
    );

    if (unshareIntegrationResponse?.isSuccessful) {
      toast.success(
        `Integration ${selectedIntegration.integration_name} has been unshared successfully!`
      );
    } else {
      toast.error(
        `Integration ${selectedIntegration.integration_name} has been unshared successfully!`
      );
    }

    handleCloseShareUnshareIntegrationDialog();
    setSelectedUsers([]);
  };

  const deternmineShareUnshareAvailableUsers = (selectedIntegration, sharedIntegrations) => {
    const sharedUsers = sharedIntegrations
      ?.filter((i) => i.integration_id === selectedIntegration.id)
      .map((i) => i.shared_user_id);

    const integrationOwner = users.find((u) => u.auth0_id === selectedIntegration.create_user_id);

    const availableUsers = isShareMode
      ? activeUsers.filter(
          (u) => !sharedUsers.includes(u.auth0_id) && u.auth0_id !== integrationOwner.auth0_id
        )
      : activeUsers.filter((u) => sharedUsers.includes(u.auth0_id));

    setAvailableShareUsers(availableUsers);
  };

  const determineIsShareUnshareDisabled = (integration, mode) => {
    const sharedUsers = sharedIntegrations
      ?.filter((i) => i.integration_id === integration.id)
      .map((i) => i.shared_user_id);

    const integrationOwner = users.find((u) => u.auth0_id === integration.create_user_id);

    const availableUsers =
      mode === "share"
        ? activeUsers.filter(
            (u) => !sharedUsers.includes(u.auth0_id) && u.auth0_id !== integrationOwner?.auth0_id
          )
        : activeUsers.filter((u) => sharedUsers.includes(u.auth0_id));

    return availableUsers.length === 0 ? true : false;
  };

  const filterIntegrations = (integration) => {
    if (isOwner || permissionChecker(userPermissions, PERMISSIONS.VIEW_ANY_INTEGRATIONS)) {
      return true;
    }

    const foundSharedIntegration = sharedIntegrations?.find(
      (shared) =>
        shared.integration_id === integration.id && shared.shared_user_id === user.cxfUserId
    );

    if (foundSharedIntegration || integration.create_user_id === user?.cxfUserId) return true;

    return false;
  };

  const handleOpenExternalUsersShareDialog = () => {
    const shareItemDataData = {
      item: "Integration",
      item_id: selectedIntegration.id,
      item_name: selectedIntegration.integration_name,
      item_tenant_id: selectedIntegration.tenant_id,
      item_owner_id: selectedIntegration.create_user_id
    };
    dispatch(setExternalUsersShareDialogOpen(true));
    dispatch(setShareItemData(shareItemDataData));
    dispatch(setOpenShareUnshareIntegrationDialog(false));
  };

  const handleExternalUsersShareDialogClose = () => {
    dispatch(setExternalUsersShareDialogOpen(false));
  };

  const handleCloseIntegrationDialog = () => {
    setShowUpdateIntegrationDialog(false);

    setTimeout(() => {
      setSelectedIntegration(null);
    }, 500);
  };

  // Filter users based on the selected integration
  useEffect(() => {
    if (selectedIntegration) {
      deternmineShareUnshareAvailableUsers(selectedIntegration, sharedIntegrations);
    }
  }, [selectedIntegration, sharedIntegrations, isShareMode]);

  const tableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "icon",
          accessor: "icon",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => (
            <SoftBox width="60px" height="60px">
              {value}
            </SoftBox>
          )
        },
        {
          Header: "name",
          accessor: "integration_name",
          width: 200,
          canGroupBy: false,
          disableSortBy: false,
          defaultSortOrder: "asc",
          sortType: (rowA, rowB, columnId) => (rowA.values[columnId] || "").localeCompare(rowB.values[columnId] || "", undefined, { sensitivity: "base" }),
          Cell: ({ value, row }) => {
            return (
              <SoftTypography fontSize="14px" fontWeight="bold">
                <Link
                  style={{ color: "#2D3748" }}
                  className="white-to-dark"
                  to={`/integration/${row.original.id}`}
                >
                  {value}
                </Link>
              </SoftTypography>
            );
          }
        },
        {
          Header: "current version",
          accessor: "current_version",
          canGroupBy: false,
          Cell: ({ value }) => (
            <SoftTypography
              fontSize="12px"
              paddingLeft="48px"
              fontWeight="bold"
            >
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "shared status",
          accessor: "shared_status",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => (
            <SoftTypography fontSize="14px">
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "status",
          accessor: "status",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => {
            return <StatusBadge status={value} />;
          }
        },
        {
          Header: "date created",
          accessor: "created_date_time",
          width: 100,
          canGroupBy: false,
          Cell: ({ value }) => (
            <SoftTypography
              fontSize="14px"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start"
              }}
            >
              <span>{value && moment(value).format("MM/DD/YYYY")}</span>
              <span>{value && moment(value).format("h:mm:ss A")}</span>
            </SoftTypography>
          )
        },
        {
          Header: "actions",
          accessor: "action",
          canGroupBy: false,
          disableSortBy: true
        },
        {
          accessor: "id",
          show: false
        }
      ].map((column) => ({ ...column, disableResize: true })),
      rows: integrations
        .filter(filterIntegrations)
        .sort((firstIntegration, secondIntegration) => (firstIntegration.integration_name || "").localeCompare(secondIntegration.integration_name || "", undefined, { sensitivity: "base" }))
        .map((integration) => {
        const hasCustomLogo = hasCustomIntegrationLogo(integration.logo);

        return {
          ...integration,
          shared_status: determineShareStatus(integration, sharedIntegrations),
          action: (
            <IntegrationsMenu
              integration={integration}
              userPermissions={userPermissions}
              setSelectedIntegration={setSelectedIntegration}
              setIsShareMode={setIsShareMode}
              isShareDisabled={determineIsShareUnshareDisabled(integration, "share")}
              isUnshareDisabled={determineIsShareUnshareDisabled(integration, "unshare")}
              sharedIntegrationAccessType={canEditIntegration(integration)}
            />
          ),
          icon: (
            <SoftBox
              component="img"
              width="100%"
              minHeight="100%"
              maxHeight="100%"
              border={hasCustomLogo ? "0.5px solid #E6E6E6" : "none"}
              borderRadius="12px"
              sx={{ objectFit: "cover" }}
              src={
                hasCustomLogo
                  ? `${integration.logo}?v=${integration.updated_date_time ?? ""}`
                  : defaultIntegrationIcon
              }
            />
          ),
          integration
        };
      })
    };
  }, [integrations, sharedIntegrations, isShareMode]);

  function handleReloadIntegrations() {
    handleFetchIntegrations();
    handleFetchSharedIntegrations();
  }

  return (
    <>
      <div
        style={{
          padding: "8px",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "var(--main-bg-color)"
        }}
      >
        <DashboardNavbar />
      </div>
      <DashboardLayout sx={{ pt: 0, pb: 0 }}>
        <SoftBox sx={{ mt: -4.2, mb: 0.5 }}>
          <Card
            sx={{
              padding: "8px 0 0 0",
              mb: -0.4,
              background: "none !important",
              boxShadow: "none"
            }}
          >
            <DataTable
              table={tableData}
              entriesPerPage={{ defaultValue: 25 }}
              hiddenTopButtons={true}
              splitted={true}
              reloadTableData={handleReloadIntegrations}
              withSearchbar={true}
              withIntegrationsOptions={true}
              options={options}
              withStatusFilter
              tableView={tableView}
              setTableView={setTableView}
              withTileview
              tileViewComponent={(rows) => <Tiles rows={rows} />}
              tableHeight="clamp(400px, calc(100vh - 83px), calc(100vh - 83px))"
            />
            <ShareUnshareDialog
              open={openShareUnshareDialog}
              isShareMode={isShareMode}
              shareUnshareItem={"Integration"}
              shareUnshareItemName={selectedIntegration?.integration_name}
              onClose={handleCloseShareUnshareIntegrationDialog}
              users={availableShareUsers}
              onAccept={isShareMode ? handleShareIntegration : handleUnshareIntegration}
              handleOpenExternalUsersShareDialog={handleOpenExternalUsersShareDialog}
            />
            <ExternalUsersShareDialog
              open={isExternalUsersShareDialogOpen}
              onClose={handleExternalUsersShareDialogClose}
              onAccept={() => console.log("accept")}
            />
          </Card>
        </SoftBox>
        {!!selectedIntegration && (
          <UpdateIntegrationDialog
            open={showUpdateIntegrationDialog}
            onClose={handleCloseIntegrationDialog}
            integration={selectedIntegration}
          />
        )}
      </DashboardLayout>
    </>
  );
};
